import { isNil, map, filter, some, includes, get, groupBy } from "lodash";
import cx from "classnames";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useDebouncedCallback } from "use-debounce";
import Input from "antd/lib/input";
import Button from "antd/lib/button";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import List from "react-virtualized/dist/commonjs/List";
import PlainButton from "@/components/plain-button/PlainButton";
import Tooltip from "@/components/Tooltip";
import useDataSourceSchema from "@/pages/queries/hooks/useDataSourceSchema";
import useImmutableCallback from "@/lib/hooks/useImmutableCallback";
import LoadingState from "../items-list/components/LoadingState";
import { useTheme } from "@/components/ThemeProvider";
import { 
  LinkOutlined, 
  ProjectOutlined, 
  CodeOutlined, 
  FolderOutlined, 
  FolderOpenOutlined,
  TableOutlined, 
  PlusOutlined,
} from "@ant-design/icons";

import "./SchemaBrowser.less";

const SchemaItemColumnType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
});

export const SchemaItemType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(SchemaItemColumnType).isRequired,
});

const schemaTableHeight = 22;
const schemaColumnHeight = 18;

function parseTableName(tableName) {
  const parts = tableName.split('_');
  if (parts[0] === 'ch' && (parts[2] === 'p' || parts[2] === 'c')) {
    let chainName, projectName, contractName, eventName;
    
    chainName = parts[1];
    
    if (parts[2] === 'p') {
      projectName = parts[3];
      contractName = parts[5];
      eventName = parts[7];
    } else {
      projectName = null;
      contractName = parts[3];
      eventName = parts[5];
    }

    return { isNewFormat: true, chainName, projectName, contractName, eventName, fullName: tableName };
  }
  return { isNewFormat: false, name: tableName, eventName: tableName };
}

function SchemaGroup({ name, items, expandedFlags, onTableExpand, onItemSelect, level = 0 }) {
  const [expanded, setExpanded] = useState(false);

  const getIcon = () => {
    switch (level) {
      case 0: return <LinkOutlined />;
      case 1: return <ProjectOutlined />;
      case 2: return <CodeOutlined />;
      default: return expanded ? <FolderOpenOutlined /> : <FolderOutlined />;
    }
  };

  const renderItems = () => {
    if (level < 3 && items[0] && items[0].items) {
      return items.map(item => (
        <SchemaGroup
          key={item.name}
          name={item.name}
          items={item.items}
          expandedFlags={expandedFlags}
          onTableExpand={onTableExpand}
          onItemSelect={onItemSelect}
          level={level + 1}
        />
      ));
    } else {
      return items.map(item => (
        <SchemaItem
          key={item.name}
          item={item}
          expanded={expandedFlags[item.name]}
          onToggle={() => onTableExpand(item.name)}
          onSelect={onItemSelect}
          level={level + 1}
        />
      ));
    }
  };

  return (
    <div className="schema-item">
      <div className="item-content">
        <PlainButton className="group-name" onClick={() => setExpanded(!expanded)}>
          {getIcon()}
          <span className="m-l-5">{name}</span>
        </PlainButton>
      </div>
      {expanded && <div className="group-items">{renderItems()}</div>}
    </div>
  );
}

function SchemaItem({ item, expanded, onToggle, onSelect, level, ...props }) {
  const parsedName = parseTableName(item.name);
  const displayName = parsedName.isNewFormat ? parsedName.eventName : parsedName.name;

  const handleSelect = useCallback(
    (event, columnName) => {
      event.preventDefault();
      event.stopPropagation();
      onSelect(columnName ? `${item.name}.${columnName}` : item.name);
    },
    [onSelect, item.name]
  );

  if (!item) {
    return null;
  }

  return (
    <div className="schema-item" {...props}>
      <div className="item-content">
        <PlainButton className="table-name" onClick={onToggle}>
          <TableOutlined />
          <span className="m-l-5" title={item.name}>{displayName}</span>
          {!isNil(item.size) && <span> ({item.size})</span>}
        </PlainButton>
        <Tooltip
          title="Insert table name into query text"
          mouseEnterDelay={0}
          mouseLeaveDelay={0}
          placement="rightTop">
          
          <div className="copy-to-editor" onClick={e => handleSelect(e)}>
            <PlusOutlined />
          </div>
        </Tooltip>
      </div>
      {expanded && (
        <div className="table-open">
          {item.loading ? (
            <div className="table-open-item">Loading...</div>
          ) : (
            map(item.columns, column => {
              const columnName = get(column, "name");
              const columnType = get(column, "type");
              return (
                <Tooltip
                  key={columnName}
                  title="Insert column name into query text"
                  mouseEnterDelay={0}
                  mouseLeaveDelay={0}
                  placement="rightTop">
                  <PlainButton className="table-open-item" onClick={e => handleSelect(e, columnName)}>
                    <div>
                      {columnName}
                      {columnType && <span className="column-type">({columnType})</span>}
                    </div>
                    <div className="copy-to-editor">
                      <PlusOutlined />
                    </div>
                  </PlainButton>
                </Tooltip>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

SchemaItem.propTypes = {
  item: SchemaItemType,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onSelect: PropTypes.func,
};

SchemaItem.defaultProps = {
  item: null,
  expanded: false,
  onToggle: () => {},
  onSelect: () => {},
};

function SchemaLoadingState() {
  return (
    <div className="schema-loading-state">
      <LoadingState className="" />
    </div>
  );
}

// Helper function to calculate group height
function calculateGroupHeight(group, expandedFlags) {
  let height = 30; // Base height for the group header

  if (group.items) {
    group.items.forEach(item => {
      height += 30; // Height for each item (table)
      if (expandedFlags[item.name] && item.columns) {
        height += item.columns.length * 24; // Approximate height for each column
      }
    });
  }

  return height;
}

export function SchemaList({ loading, schema, expandedFlags, onTableExpand, onItemSelect }) {
  const [listRef, setListRef] = useState(null);

  useEffect(() => {
    if (listRef) {
      listRef.recomputeRowHeights();
    }
  }, [listRef, schema, expandedFlags]);

  const groupedSchema = useMemo(() => {
    const groups = groupBy(schema, item => {
      const parsedName = parseTableName(item.name);
      return parsedName.isNewFormat ? parsedName.chainName : 'Other Tables';
    });

    return Object.entries(groups).map(([chainName, items]) => {
      if (chainName === 'Other Tables') {
        return { name: chainName, items };
      }

      const projectGroups = groupBy(items, item => {
        const parsedName = parseTableName(item.name);
        return parsedName.projectName || 'No Project';
      });

      const projectItems = Object.entries(projectGroups).map(([projectName, projectItems]) => {
        const contractGroups = groupBy(projectItems, item => parseTableName(item.name).contractName);

        const contractItems = Object.entries(contractGroups).map(([contractName, contractItems]) => ({
          name: contractName,
          items: contractItems
        }));

        return {
          name: projectName,
          items: contractItems
        };
      });

      return {
        name: chainName,
        items: projectItems
      };
    });
  }, [schema]);

  return (
    <div className="schema-browser">
      {loading && <SchemaLoadingState />}
      {!loading && (
        <AutoSizer>
          {({ width, height }) => (
            <List
              ref={setListRef}
              width={width}
              height={height}
              rowCount={groupedSchema.length}
              rowHeight={({ index }) => calculateGroupHeight(groupedSchema[index], expandedFlags)}
              rowRenderer={({ key, index, style }) => {
                const group = groupedSchema[index];
                return (
                  <SchemaGroup
                    key={key}
                    style={style}
                    name={group.name}
                    items={group.items}
                    expandedFlags={expandedFlags}
                    onTableExpand={onTableExpand}
                    onItemSelect={onItemSelect}
                  />
                );
              }}
            />
          )}
        </AutoSizer>
      )}
    </div>
  );
}

export function applyFilterOnSchema(schema, filterString) {
  const filters = filter(filterString.toLowerCase().split(/\s+/), s => s.length > 0);

  // Empty string: return original schema
  if (filters.length === 0) {
    return schema;
  }

  // Single word: matches table or column
  if (filters.length === 1) {
    const nameFilter = filters[0];
    const columnFilter = filters[0];
    return filter(
      schema,
      item =>
        includes(item.name.toLowerCase(), nameFilter) ||
        some(item.columns, column => includes(get(column, "name").toLowerCase(), columnFilter))
    );
  }

  // Two (or more) words: first matches table, seconds matches column
  const nameFilter = filters[0];
  const columnFilter = filters[1];
  return filter(
    map(schema, item => {
      if (includes(item.name.toLowerCase(), nameFilter)) {
        item = {
          ...item,
          columns: filter(item.columns, column => includes(get(column, "name").toLowerCase(), columnFilter)),
        };
        return item.columns.length > 0 ? item : null;
      }
    })
  );
}

export default function SchemaBrowser({
  dataSource,
  onSchemaUpdate,
  onItemSelect,
  options,
  onOptionsUpdate,
  ...props
}) {
  const [schema, isLoading, refreshSchema] = useDataSourceSchema(dataSource);
  const [filterString, setFilterString] = useState("");
  const filteredSchema = useMemo(() => applyFilterOnSchema(schema, filterString), [schema, filterString]);
  const [handleFilterChange] = useDebouncedCallback(setFilterString, 500);
  const [expandedFlags, setExpandedFlags] = useState({});
  const { isDarkMode } = useTheme();

  const handleSchemaUpdate = useImmutableCallback(onSchemaUpdate);

  useEffect(() => {
    setExpandedFlags({});
    handleSchemaUpdate(schema);
  }, [schema, handleSchemaUpdate]);

  if (schema.length === 0 && !isLoading) {
    return null;
  }

  function toggleTable(tableName) {
    setExpandedFlags({
      ...expandedFlags,
      [tableName]: !expandedFlags[tableName],
    });
  }

  return (
    <div className={cx("schema-container", { "dark-mode": isDarkMode })} {...props}>
      <div className="schema-control">
        <Input
          className="m-r-5"
          placeholder="Search schema..."
          aria-label="Search schema"
          disabled={schema.length === 0}
          onChange={event => handleFilterChange(event.target.value)}
        />

        <Tooltip title="Refresh Schema">
          <Button onClick={() => refreshSchema(true)}>
            <i className={cx("zmdi zmdi-refresh", { "zmdi-hc-spin": isLoading })} aria-hidden="true" />
            <span className="sr-only">{isLoading ? "Loading, please wait." : "Press to refresh."}</span>
          </Button>
        </Tooltip>
      </div>
      <SchemaList
        loading={isLoading && schema.length === 0}
        schema={filteredSchema}
        expandedFlags={expandedFlags}
        onTableExpand={toggleTable}
        onItemSelect={onItemSelect}
      />
    </div>
  );
}

SchemaBrowser.propTypes = {
  dataSource: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onSchemaUpdate: PropTypes.func,
  onItemSelect: PropTypes.func,
};

SchemaBrowser.defaultProps = {
  dataSource: null,
  onSchemaUpdate: () => {},
  onItemSelect: () => {},
};